export type CookieEntry = {
    key: string
    label: string
    type: 'cookie' | 'localStorage' | 'sessionStorage'
    category: 'essential' | 'non-essential'
    purpose: string
    expires: string
    match?: 'prefix'
    aliases?: string[]
    removeOnReject?: boolean
    i18nKey?: string
}

export const COOKIE_INVENTORY: CookieEntry[] = [
    {
        key: 'essential',
        i18nKey: 'essential',
        label: 'Essential consent',
        type: 'cookie',
        category: 'essential',
        purpose: 'Stores your cookie settings.',
        expires: '12 months'
    },
    {
        key: 'marketing',
        i18nKey: 'marketing',
        label: 'Marketing consent',
        type: 'cookie',
        category: 'non-essential',
        purpose: 'Stores whether marketing tools are allowed.',
        expires: '12 months'
    },
    {
        key: 'cookie-consent',
        i18nKey: 'cookie_consent',
        label: 'Historické nastavenie súhlasu',
        type: 'localStorage',
        category: 'essential',
        purpose: 'Legacy consent flag for older components.',
        expires: 'No expiry (localStorage)'
    },
    {
        key: 'cookie-consent-essential',
        label: 'Cookie súhlas (essential)',
        type: 'localStorage',
        category: 'essential',
        purpose: 'Fallback consent state for essential cookies.',
        expires: 'No expiry (localStorage)'
    },
    {
        key: 'cookie-consent-marketing',
        label: 'Cookie súhlas (marketing)',
        type: 'localStorage',
        category: 'essential',
        purpose: 'Fallback consent state for marketing cookies.',
        expires: 'No expiry (localStorage)'
    },
    {
        key: 'storage-data-version',
        i18nKey: 'storage_data_version',
        label: 'Verzia dát v úložisku',
        type: 'localStorage',
        category: 'essential',
        purpose: 'Keeps version to clear incompatible storage.',
        expires: 'No expiry (localStorage)'
    },
    {
        key: 'auth/token',
        i18nKey: 'auth_token',
        label: 'Prihlasovací token',
        type: 'localStorage',
        category: 'essential',
        purpose: 'Maintains user authentication session.',
        expires: 'Until sign out or cleared by browser'
    },
    {
        key: 'deviceId',
        i18nKey: 'deviceId',
        label: 'ID zariadenia',
        type: 'localStorage',
        category: 'essential',
        purpose: 'Remembers device for remember-me login.',
        expires: 'No expiry (localStorage)'
    },
    {
        key: 'deviceToken',
        i18nKey: 'deviceToken',
        label: 'Token zariadenia',
        type: 'localStorage',
        category: 'essential',
        purpose: 'Token for trusted device login.',
        expires: 'Until sign out or cleared by browser'
    },
    {
        key: 'uss.id',
        i18nKey: 'uss_id',
        label: 'Perzistentná session',
        type: 'localStorage',
        category: 'essential',
        purpose: 'Identifies session for API requests.',
        expires: 'No expiry (localStorage)'
    },
    {
        key: 'order',
        i18nKey: 'order',
        label: 'Rozpracovaná objednávka',
        type: 'localStorage',
        category: 'essential',
        purpose: 'Stores in-progress order and billing data.',
        expires: 'No expiry (localStorage)'
    },
    {
        key: 'user.lang',
        i18nKey: 'user_lang',
        label: 'Preferovaný jazyk',
        type: 'localStorage',
        category: 'essential',
        purpose: 'Keeps selected site language.',
        expires: 'No expiry (localStorage)'
    },
    {
        key: 'infoMessages',
        i18nKey: 'infoMessages',
        label: 'Prečítané info správy',
        type: 'localStorage',
        category: 'essential',
        purpose: 'Tracks which info messages were read.',
        expires: 'No expiry (localStorage)'
    },
    {
        key: 'coords',
        i18nKey: 'coords',
        label: 'Posledná poloha',
        type: 'localStorage',
        category: 'essential',
        purpose: 'Stores coordinates for nearest warehouse.',
        expires: 'Overwritten within minutes'
    },
    {
        key: 'coordsTimestamp',
        i18nKey: 'coordsTimestamp',
        label: 'Čas uloženia polohy',
        type: 'localStorage',
        category: 'essential',
        purpose: 'Timestamp of stored coordinates.',
        expires: 'Overwritten within minutes'
    },
    {
        key: 'geoOverride:',
        i18nKey: 'geoOverride',
        label: 'Dočasné prepísanie skladu',
        type: 'localStorage',
        category: 'essential',
        purpose: 'Keeps manual warehouse/box override.',
        expires: 'Until override timer expires',
        match: 'prefix'
    },
    {
        key: 'geoAllowed',
        i18nKey: 'geoAllowed',
        label: 'Súhlas s geolokáciou',
        type: 'sessionStorage',
        category: 'essential',
        purpose: 'Remember location permission in this session.',
        expires: 'Session'
    },
    {
        key: 'setRedirect',
        i18nKey: 'setRedirect',
        label: 'Presmerovanie v natívnej appke',
        type: 'sessionStorage',
        category: 'essential',
        purpose: 'Controls redirect between native app and web.',
        expires: 'Session'
    },
    {
        key: 'setRedirectPersistent',
        label: 'Perzistentné presmerovanie v natívnej appke',
        type: 'localStorage',
        category: 'essential',
        purpose: 'Persists native app redirect state between app launches.',
        expires: 'No expiry (localStorage)'
    },
    {
        key: 'suppressOverdueModalOnce',
        i18nKey: 'suppressOverdueModalOnce',
        label: 'Skrytie upozornenia na nedoplatok',
        type: 'sessionStorage',
        category: 'essential',
        purpose: 'Prevents repeated overdue alert in session.',
        expires: 'Session'
    },
    {
        key: '___imfo_tab_seen___',
        i18nKey: 'imfo_tab_seen',
        label: 'Identifikácia prvej karty',
        type: 'sessionStorage',
        category: 'essential',
        purpose: 'Syncs info messages across tabs.',
        expires: 'Session'
    },
    {
        key: '_ga',
        i18nKey: '_ga',
        label: 'Google Analytics',
        type: 'cookie',
        category: 'non-essential',
        purpose: 'Traffic analytics cookie.',
        expires: '2 years',
        removeOnReject: true
    },
    {
        key: '_ga_',
        i18nKey: '_ga_prefix',
        label: 'Google Analytics (property)',
        type: 'cookie',
        category: 'non-essential',
        purpose: 'Property-specific analytics cookie.',
        expires: '2 years',
        match: 'prefix',
        removeOnReject: true
    },
    {
        key: '_gcl_au',
        i18nKey: '_gcl_au',
        label: 'Google Ads / Conversion Linker',
        type: 'cookie',
        category: 'non-essential',
        purpose: 'Measures ads and conversions.',
        expires: '90 days',
        removeOnReject: true
    },
    {
        key: '_gcl_aw',
        i18nKey: '_gcl_aw',
        label: 'Google Ads',
        type: 'cookie',
        category: 'non-essential',
        purpose: 'Measures ads and conversions.',
        expires: '90 days',
        removeOnReject: true
    },
    {
        key: '_gcl_dc',
        i18nKey: '_gcl_dc',
        label: 'Google Ads',
        type: 'cookie',
        category: 'non-essential',
        purpose: 'Measures ads and conversions.',
        expires: '90 days',
        removeOnReject: true
    },
    {
        key: '_fbp',
        i18nKey: '_fbp',
        label: 'Meta Pixel',
        type: 'cookie',
        category: 'non-essential',
        purpose: 'Measures Facebook/Instagram ads.',
        expires: '90 days',
        removeOnReject: true
    },
    {
        key: 'ecm',
        i18nKey: 'ecomail',
        label: 'Ecomail',
        type: 'cookie',
        category: 'non-essential',
        purpose: 'Shows newsletter popup and measures campaign behaviour.',
        expires: 'Up to 12 months',
        match: 'prefix',
        removeOnReject: true
    },
    {
        key: '_fbc',
        i18nKey: '_fbc',
        label: 'Meta Pixel click id',
        type: 'cookie',
        category: 'non-essential',
        purpose: 'Stores Meta ad click id.',
        expires: '90 days',
        removeOnReject: true
    },
    {
        key: 'gclid',
        i18nKey: 'gclid',
        label: 'Google Ads click id',
        type: 'cookie',
        category: 'non-essential',
        purpose: 'Stores Google ad click id.',
        expires: '90 days',
        removeOnReject: true
    },
    {
        key: 'fbclid',
        i18nKey: 'fbclid',
        label: 'Meta click id',
        type: 'cookie',
        category: 'non-essential',
        purpose: 'Stores Facebook/Instagram ad click id.',
        expires: '90 days',
        removeOnReject: true
    },
    {
        key: '_clck',
        i18nKey: '_clck',
        label: 'Microsoft Clarity',
        type: 'cookie',
        category: 'non-essential',
        purpose: 'Stores the Clarity user ID and preferences.',
        expires: '1 year',
        removeOnReject: true
    },
    {
        key: '_clsk',
        i18nKey: '_clsk',
        label: 'Microsoft Clarity session',
        type: 'cookie',
        category: 'non-essential',
        purpose: 'Links multiple page views into a single Clarity session.',
        expires: '1 day',
        removeOnReject: true
    },
    {
        key: '_hjSessionUser_',
        i18nKey: '_hjSessionUser',
        label: 'Hotjar session user',
        type: 'cookie',
        category: 'non-essential',
        purpose: 'Persists a unique ID for Hotjar across sessions.',
        expires: '1 year',
        match: 'prefix',
        removeOnReject: true
    },
    {
        key: '_hjSession_',
        i18nKey: '_hjSession',
        label: 'Hotjar session',
        type: 'cookie',
        category: 'non-essential',
        purpose: 'Stores current Hotjar session data.',
        expires: '30 minutes',
        match: 'prefix',
        removeOnReject: true
    },
    {
        key: '_hjFirstSeen',
        i18nKey: '_hjFirstSeen',
        label: 'Hotjar first visit',
        type: 'cookie',
        category: 'non-essential',
        purpose: 'Identifies first session for new user.',
        expires: '30 minutes',
        removeOnReject: true
    },
    {
        key: '_hjIncludedInPageviewSample',
        i18nKey: '_hjIncludedInPageviewSample',
        label: 'Hotjar pageview sample',
        type: 'cookie',
        category: 'non-essential',
        purpose: 'Determines if user is in pageview sampling.',
        expires: '2 minutes',
        removeOnReject: true
    },
    {
        key: '_hjIncludedInSessionSample',
        i18nKey: '_hjIncludedInSessionSample',
        label: 'Hotjar session sample',
        type: 'cookie',
        category: 'non-essential',
        purpose: 'Determines if user is in session sampling.',
        expires: '2 minutes',
        removeOnReject: true
    },
    {
        key: '_hjIncludedInSessionSample_',
        i18nKey: '_hjIncludedInSessionSample_prefix',
        label: 'Hotjar session sample (site)',
        type: 'cookie',
        category: 'non-essential',
        purpose: 'Determines if user is in session sampling (site-specific).',
        expires: '2 minutes',
        match: 'prefix',
        removeOnReject: true
    },
    {
        key: '_hjAbsoluteSessionInProgress',
        i18nKey: '_hjAbsoluteSessionInProgress',
        label: 'Hotjar session progress',
        type: 'cookie',
        category: 'non-essential',
        purpose: 'Tracks start of a session for counting visits.',
        expires: '30 minutes',
        removeOnReject: true
    },
    {
        key: '_hjRecordingLastActivity',
        i18nKey: '_hjRecordingLastActivity',
        label: 'Hotjar recording activity',
        type: 'cookie',
        category: 'non-essential',
        purpose: 'Stores last recorded activity timestamp.',
        expires: 'Session',
        removeOnReject: true
    },
    {
        key: '_hjTLDTest',
        i18nKey: '_hjTLDTest',
        label: 'Hotjar top-level domain test',
        type: 'cookie',
        category: 'non-essential',
        purpose: 'Tests cookies across subdomains to find TLD.',
        expires: 'Session',
        removeOnReject: true
    }
]

export const COOKIE_KEY_ALIASES: Record<string, string> = {
    IMFO_MESSAGES_STORAGE_KEY: 'infoMessages',
    DEVICE_ID_STORAGE_KEY: 'deviceId',
    DEVICE_TOKEN_STORAGE_KEY: 'deviceToken',
    GEO_ALLOWED_KEY: 'geoAllowed'
}
